import * as React from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, cn } from "@finster/ui";
import { Check, ChevronDown, Database } from "lucide-react";
import { getAvailableFinsterQueryDataSourceGroups } from "../../api/finster/constants";
import type { DataSourceFeatureFlags, FinsterQueryDataSource } from "../../api/finster/constants";

interface DataSourceSelectorProps {
  selectedDataSources: readonly FinsterQueryDataSource[];
  onDataSourcesChange: (dataSources: FinsterQueryDataSource[]) => void;
  dataSourceFeatureFlags?: DataSourceFeatureFlags;
  dataSourceFeatureFlagsLoading?: boolean;
  disabled?: boolean;
  density?: "default" | "compact";
}

const MENU_GAP_PX = 8;
const MENU_VIEWPORT_PADDING_PX = 16;
const MENU_MAX_HEIGHT_PX = 440;
const MENU_MAX_HEIGHT_ABOVE_PX = 380;
const COMPACT_MENU_WIDTH_PX = 380;
const DEFAULT_MENU_WIDTH_PX = 320;
const MENU_MIN_USEFUL_BELOW_PX = 240;

export const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({
  selectedDataSources,
  onDataSourcesChange,
  dataSourceFeatureFlags,
  dataSourceFeatureFlagsLoading = false,
  disabled = false,
  density = "default",
}) => {
  const isCompact = density === "compact";
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    side: "top" | "bottom";
    left: number;
    verticalOffset: number;
    maxHeight: number;
    width: number;
  }>({
    side: "top",
    left: 0,
    verticalOffset: 0,
    maxHeight: MENU_MAX_HEIGHT_PX,
    width: DEFAULT_MENU_WIDTH_PX,
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);
  const availableGroups = useMemo(
    () => getAvailableFinsterQueryDataSourceGroups(dataSourceFeatureFlags, dataSourceFeatureFlagsLoading),
    [dataSourceFeatureFlags, dataSourceFeatureFlagsLoading]
  );
  const availableDataSources = useMemo(
    () => availableGroups.flatMap((group) => group.children.map((child) => child.param)),
    [availableGroups]
  );
  const selectedSourceSet = useMemo(() => new Set(selectedDataSources), [selectedDataSources]);
  const allSourcesSelected =
    availableDataSources.length > 0 &&
    availableDataSources.every((source) => selectedSourceSet.has(source));

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !menuContentRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updateMenuPosition = () => {
      const triggerRect = menuRef.current?.getBoundingClientRect();
      if (!triggerRect) return;
      const preferredWidth = isCompact ? COMPACT_MENU_WIDTH_PX : DEFAULT_MENU_WIDTH_PX;
      const width = Math.min(
        preferredWidth,
        Math.max(240, window.innerWidth - MENU_VIEWPORT_PADDING_PX * 2)
      );

      const availableAbove = triggerRect.top - MENU_VIEWPORT_PADDING_PX - MENU_GAP_PX;
      const availableBelow =
        window.innerHeight - triggerRect.bottom - MENU_VIEWPORT_PADDING_PX - MENU_GAP_PX;
      const side =
        availableBelow >= MENU_MIN_USEFUL_BELOW_PX || availableBelow >= availableAbove
          ? "bottom"
          : "top";
      const availableHeight = side === "top" ? availableAbove : availableBelow;
      const preferredMaxHeight =
        side === "top" ? MENU_MAX_HEIGHT_ABOVE_PX : MENU_MAX_HEIGHT_PX;
      const maxHeight = Math.max(0, Math.min(preferredMaxHeight, Math.floor(availableHeight)));
      const maxLeft = Math.max(
        MENU_VIEWPORT_PADDING_PX,
        window.innerWidth - MENU_VIEWPORT_PADDING_PX - width
      );

      setMenuPosition({
        side,
        // In compact taskpanes, bias the menu to the right edge when possible
        // so long source labels get more usable line length.
        left: Math.min(
          Math.max(MENU_VIEWPORT_PADDING_PX, isCompact ? triggerRect.right - width : triggerRect.left),
          maxLeft
        ),
        verticalOffset:
          side === "top"
            ? window.innerHeight - triggerRect.top + MENU_GAP_PX
            : triggerRect.bottom + MENU_GAP_PX,
        maxHeight,
        width,
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isCompact, open]);

  const getSourceButtonText = () => {
    if (selectedDataSources.length === 0) return "Off";
    if (allSourcesSelected) return "All";
    return String(selectedDataSources.length);
  };

  const toggleAllSources = () => {
    onDataSourcesChange(allSourcesSelected ? [] : [...availableDataSources]);
  };

  const toggleSource = (source: FinsterQueryDataSource) => {
    onDataSourcesChange(
      selectedSourceSet.has(source)
        ? selectedDataSources.filter((item) => item !== source)
        : [...selectedDataSources, source]
    );
  };

  const toggleGroup = (sources: readonly FinsterQueryDataSource[]) => {
    const allGroupSourcesSelected = sources.every((source) => selectedSourceSet.has(source));
    onDataSourcesChange(
      allGroupSourcesSelected
        ? selectedDataSources.filter((source) => !sources.includes(source))
        : [...selectedDataSources.filter((source) => !sources.includes(source)), ...sources]
    );
  };

  return (
    <div ref={menuRef} className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
        aria-label={`Data Sources: ${getSourceButtonText()}`}
        aria-expanded={open}
        className={cn(
          "cursor-pointer rounded-md bg-background hover:border-primary hover:bg-primary/5 hover:text-foreground",
          isCompact ? "h-8 gap-1.5 px-2.5" : "h-9 gap-2 px-3"
        )}
      >
        <Database className="h-4 w-4" aria-hidden />
        <span>{getSourceButtonText()}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </Button>

      {open &&
        createPortal(
        <div
          ref={menuContentRef}
          className={cn(
            "fixed z-[2147483647] overflow-y-auto rounded-xl border border-border bg-background text-foreground shadow-xl"
          )}
          style={{
            left: menuPosition.left,
            width: menuPosition.width,
            maxHeight: menuPosition.maxHeight,
            ...(menuPosition.side === "top"
              ? { bottom: menuPosition.verticalOffset }
              : { top: menuPosition.verticalOffset }),
          }}
        >
          <div
            className={cn(
              "sticky top-0 z-10 flex items-center justify-between gap-3 bg-background",
              isCompact ? "px-2.5 pb-2 pt-2.5" : "px-3 pb-3 pt-3"
            )}
          >
            <p className={cn("m-0 font-semibold", isCompact ? "text-xs" : "text-sm")}>
              Data sources
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={toggleAllSources}
              className={cn(
                "cursor-pointer rounded-md text-xs hover:border-primary hover:bg-primary/5 hover:text-foreground",
                isCompact ? "h-7 px-2.5" : "h-8 px-3"
              )}
            >
              {allSourcesSelected ? "Deselect All" : "Select All"}
            </Button>
          </div>

          <div className={cn("flex flex-col", isCompact ? "gap-1 px-2.5 pb-2.5" : "gap-2 px-3 pb-3")}>
            {availableGroups.map((group) => {
              const groupSources = group.children.map((child) => child.param);
              const selectedInGroup = groupSources.filter((source) => selectedSourceSet.has(source));
              const groupChecked = selectedInGroup.length === groupSources.length;
              const groupPartial =
                selectedInGroup.length > 0 && selectedInGroup.length < groupSources.length;

              return (
                <div key={group.label}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupSources)}
                    className={cn(
                      "flex w-full cursor-pointer items-start rounded-md border-0 bg-transparent px-1 text-left font-medium text-foreground hover:bg-accent",
                      isCompact ? "gap-1.5 py-1 text-xs" : "gap-2 py-1.5 text-sm"
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex flex-none items-center justify-center rounded border border-border text-primary",
                        isCompact ? "size-3.5 min-h-3.5 min-w-3.5" : "size-4 min-h-4 min-w-4",
                        (groupChecked || groupPartial) && "border-primary bg-primary-muted"
                      )}
                      aria-hidden
                    >
                      {groupChecked ? (
                        <Check className="h-3 w-3" />
                      ) : groupPartial ? (
                        <span className="h-0.5 w-2 rounded bg-primary" />
                      ) : null}
                    </span>
                    <span>{group.label}</span>
                  </button>
                  <div className={cn("border-l border-border", isCompact ? "ml-2 pl-2" : "ml-3 pl-3")}>
                    {group.children.map((source) => {
                      const checked = selectedSourceSet.has(source.param);
                      return (
                        <button
                          key={source.param}
                          type="button"
                          onClick={() => toggleSource(source.param)}
                          className={cn(
                            "flex w-full cursor-pointer items-start rounded-md border-0 bg-transparent px-1 text-left text-xs text-foreground hover:bg-accent",
                            isCompact ? "gap-1.5 py-0.5" : "gap-2 py-1"
                          )}
                        >
                          <span
                            className={cn(
                              "mt-0.5 flex flex-none items-center justify-center rounded border border-border text-primary",
                              isCompact ? "size-3.5 min-h-3.5 min-w-3.5" : "size-4 min-h-4 min-w-4",
                              checked && "border-primary bg-primary-muted"
                            )}
                            aria-hidden
                          >
                            {checked && <Check className="h-3 w-3" />}
                          </span>
                          <span>{source.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>,
          document.body
        )}
    </div>
  );
};
